import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { TopBar } from '@/components/TopBar';
import { EventCard } from '@/components/EventCard';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { useAuthStore } from '@/lib/store';
import { toast } from 'sonner';

export default function EventsListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('upcoming');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get('/events?scope=mine');
        if (mounted) setEvents(r.data || []);
      } catch (err) {
        console.error('Failed to load events:', err);
        toast.error('Failed to load events');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const visible = useMemo(() => {
    let list = events;
    if (filter !== 'all') list = list.filter((e) => e.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => (e.title || '').toLowerCase().includes(q) || (e.location_text || '').toLowerCase().includes(q));
    }
    return list;
  }, [events, filter, search]);

  return (
    <div data-testid="events-list-page">
      <TopBar title="Events" subtitle="Plan, gather, remember." user={user} />
      <div className="mb-3">
        <SegmentedTabs
          testid="events-filter-segmented"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'ongoing', label: 'Ongoing' },
            { value: 'past', label: 'Past' },
            { value: 'all', label: 'All' },
          ]}
        />
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="events-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events…" className="pl-9 rounded-xl" />
      </div>
      {loading ? (
        <div className="space-y-3" data-testid="events-loading">
          {[0,1,2].map((i) => <div key={i} className="h-44 rounded-2xl bg-secondary animate-pulse" />)}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          title={filter === 'past' ? 'No past events yet' : 'Nothing here yet'}
          description={filter === 'past' ? 'Past trips and events will appear here once their date passes.' : 'Create your first event — a road trip, vacation, or conference.'}
          action={<Button data-testid="events-empty-cta" onClick={() => navigate('/events/new')} className="rounded-xl gradient-fab text-white h-11 px-5"><Plus className="h-4 w-4 mr-2" />Create event</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="events-list">
          {visible.map((ev) => <EventCard key={ev.id} event={ev} />)}
        </div>
      )}
    </div>
  );
}
